import { API } from './interceptor'
import { postGrowthEngineRecommendationInteraction } from './growthEngineApi'

/**
 * Phase 4 — Ollie findings, opening, finding threads and actions (Express).
 *
 * Rules find, Ollie explains: every figure here comes from Express evidence.
 * The dashboard never works out counts, £ or ages itself.
 */

export const OLLIE_PLATFORM = 'dashboard'

/** Where a finding was seen / tapped — sent as `metadata.surface`. */
export const OLLIE_FINDING_SURFACES = Object.freeze({
  PANEL: 'ollie_panel',
  THREAD: 'ollie_thread',
  SUCCESS_HUB: 'success_hub',
  FEATURE_PAGE: 'feature_page',
})

/** Same ids as Express `OLLIE_FINDING_CHIPS`. Why? / How? go as messages. */
export const OLLIE_ASK_CHIP_IDS = Object.freeze(['why', 'how'])
export const OLLIE_ACTION_CHIP_IDS = Object.freeze([
  'show_me',
  'help_me',
  'do_it',
])

/** Same as Express `OLLIE_DRAFT_USED_VIA`. OMS never sends drafts. */
export const OLLIE_DRAFT_USED_VIA = Object.freeze(['copy', 'email', 'whatsapp'])

/** Same ids / labels as Express `OLLIE_REPORT_REASONS` ("other" needs a comment). */
export const OLLIE_REPORT_REASONS = Object.freeze([
  Object.freeze({ id: 'not_true', label: "This isn't true" }),
  Object.freeze({ id: 'numbers_wrong', label: 'The numbers are wrong' }),
  Object.freeze({ id: 'already_sorted', label: 'Already sorted' }),
  Object.freeze({ id: 'not_for_us', label: "Doesn't apply to us" }),
  Object.freeze({ id: 'other', label: 'Something else' }),
])

export const OLLIE_INTENT_PREFIX = 'ollie:'

export const ollieIntentIdForCheck = checkId =>
  `${OLLIE_INTENT_PREFIX}${checkId}`

const text = value => {
  if (value == null) return null
  const trimmed = String(value).trim()
  return trimmed || null
}

const wholeOrNull = value => (Number.isFinite(value) ? value : null)

const penniesOrNull = value => (Number.isInteger(value) ? value : null)

/** Where the action lands (product, section…): plain strings only. */
const mapActionParams = params => {
  if (!params || typeof params !== 'object') return {}
  const kept = Object.entries(params).filter(
    ([, value]) => typeof value === 'string' && value.trim(),
  )
  return kept.length ? { params: Object.fromEntries(kept) } : {}
}

const mapAction = action => {
  if (!action?.id || !action?.label) return null
  return {
    id: String(action.id),
    kind: action.kind || 'open',
    label: String(action.label),
    target: action.target ?? null,
    requiresConfirm: Boolean(action.requiresConfirm),
    ...mapActionParams(action.params),
  }
}

/** One served finding. Unknown shapes are dropped rather than guessed at. */
export function mapOllieFinding(finding) {
  if (!finding?.checkId || !finding?.copy?.title) return null
  return {
    checkId: String(finding.checkId),
    intentId: finding.intentId || ollieIntentIdForCheck(finding.checkId),
    kind: finding.kind || 'act',
    goodNews: Boolean(finding.goodNews),
    category: finding.category || null,
    featureId: finding.featureId || null,
    rank: wholeOrNull(finding.rank),
    band: finding.band || null,
    fingerprint: finding.fingerprint || null,
    count: wholeOrNull(finding.count),
    totalPennies: penniesOrNull(finding.totalPennies),
    measuredCount: wholeOrNull(finding.measuredCount),
    oldestAgeDays: wholeOrNull(finding.oldestAgeDays),
    items: Array.isArray(finding.items) ? finding.items : [],
    asOf: finding.asOf || null,
    firstSeenAt: finding.firstSeenAt || null,
    newSinceLastRun: Boolean(finding.newSinceLastRun),
    carriedForward: Boolean(finding.carriedForward),
    reopenedWithNewEvidence: Boolean(finding.reopenedWithNewEvidence),
    newForYou:
      typeof finding.newForYou === 'boolean' ? finding.newForYou : null,
    aboveHighThreshold: Boolean(finding.aboveHighThreshold),
    todayJobEligible: Boolean(finding.todayJobEligible),
    estimatedMinutes: wholeOrNull(finding.estimatedMinutes),
    copy: {
      title: String(finding.copy.title),
      noticed: text(finding.copy.noticed),
      whyItMatters: text(finding.copy.whyItMatters),
      nextStep: text(finding.copy.nextStep),
    },
    actions: (finding.actions || []).map(mapAction).filter(Boolean),
    askOllie: text(finding.askOllie?.question)
      ? { question: text(finding.askOllie.question) }
      : null,
  }
}

export function mapOllieFindingsPayload(response = {}) {
  return {
    plan: response.plan ?? null,
    platform: response.platform || OLLIE_PLATFORM,
    asOf: response.asOf || null,
    stale: Boolean(response.stale),
    refreshing: Boolean(response.refreshing),
    quiet: response.quiet !== false,
    maxVisible: wholeOrNull(response.maxVisible) ?? 3,
    findings: (response.findings || []).map(mapOllieFinding).filter(Boolean),
  }
}

export function mapOllieOpeningPayload(response = {}) {
  return {
    kind: response.kind || 'intro',
    message: text(response.message) || '',
    lines: Array.isArray(response.lines) ? response.lines.filter(Boolean) : [],
    winLine: text(response.winLine),
    text: text(response.text) || text(response.message) || '',
    asOf: response.asOf || null,
    asOfLabel: text(response.asOfLabel),
    stale: Boolean(response.stale),
    findings: (response.findings || []).map(mapOllieFinding).filter(Boolean),
    todayJob: response.todayJob || null,
    starterPrompts: Array.isArray(response.starterPrompts)
      ? response.starterPrompts
      : [],
    moment: response.moment || null,
    meetCard: response.meetCard || null,
    brief: response.brief || null,
    feedback:
      text(response.feedback?.label) && text(response.feedback?.mailto)
        ? {
            label: text(response.feedback.label),
            mailto: text(response.feedback.mailto),
          }
        : null,
    supportFallback: response.supportFallback || '',
  }
}

const requireValue = (value, name) => {
  if (!value) throw new Error(`${name} is required`)
}

/** Findings this person can act on, ranked (max 3 on screen). */
export async function fetchOllieFindings(venueId) {
  requireValue(venueId, 'venueId')
  const response = await API.get(
    'api2',
    `growth-engine/venues/${venueId}/ollie/findings`,
    {
      queryStringParameters: { platform: OLLIE_PLATFORM },
    },
  )
  if (!response?.success) {
    throw new Error(response?.message || 'Unable to load what Ollie noticed')
  }
  return mapOllieFindingsPayload(response)
}

/** Opening message: findings → today's job → quiet day (templates, no model). */
export async function fetchOllieOpening({ venueId, pageContext = '' } = {}) {
  requireValue(venueId, 'venueId')
  const response = await API.get('api2', 'ollie/opening', {
    queryStringParameters: { venueId, platform: OLLIE_PLATFORM, pageContext },
  })
  return mapOllieOpeningPayload(response || {})
}

/** Tap a finding's chip (Show me / Help me / Do it for me). Saves nothing. */
export async function postOllieChip({
  conversationId,
  chipId,
  venueId = '',
} = {}) {
  requireValue(conversationId, 'conversationId')
  requireValue(chipId, 'chipId')
  if (OLLIE_ASK_CHIP_IDS.includes(chipId)) {
    throw new Error('Send Why? and How? as a message')
  }
  const response = await API.post(
    'api2',
    `ollie/conversations/${conversationId}/chips/${chipId}`,
    {
      body: venueId ? { venueId } : {},
    },
  )
  return {
    chip: response?.chip || null,
    action: response?.action ? mapAction(response.action) : null,
    finding: response?.finding || null,
  }
}

/** Tap a button on one of Ollie's replies. Saves nothing. */
export async function postOllieReplyButton({
  conversationId,
  messageId,
  index,
  venueId = '',
} = {}) {
  requireValue(conversationId, 'conversationId')
  requireValue(messageId, 'messageId')
  if (!Number.isInteger(index) || index < 0)
    throw new Error('index is required')
  const response = await API.post(
    'api2',
    `ollie/conversations/${conversationId}/messages/${messageId}/actions/${index}`,
    { body: venueId ? { venueId } : {} },
  )
  return {
    action: response?.action ? mapAction(response.action) : null,
    finding: response?.finding || null,
  }
}

const actionBody = ({ venueId, checkId }) => {
  requireValue(venueId, 'venueId')
  requireValue(checkId, 'checkId')
  return { venueId, checkId, platform: OLLIE_PLATFORM }
}

/** Build a "do it for me" / draft for review. Saves nothing. */
export async function prepareOllieAction({ venueId, checkId, actionId } = {}) {
  requireValue(actionId, 'actionId')
  const response = await API.post('api2', `ollie/actions/${actionId}/prepare`, {
    body: actionBody({ venueId, checkId }),
  })
  return {
    actionId: response?.actionId || actionId,
    finding: response?.finding || null,
    prepared: response?.prepared || null,
  }
}

/**
 * Save a prepared action. Only call after the user has pressed confirm:
 * Express refuses anything without `confirmed: true`.
 */
export async function confirmOllieAction({
  venueId,
  checkId,
  actionId,
  items,
  fields = {},
} = {}) {
  requireValue(actionId, 'actionId')
  const response = await API.post('api2', `ollie/actions/${actionId}/confirm`, {
    body: {
      ...fields,
      ...actionBody({ venueId, checkId }),
      ...(items !== undefined ? { items } : {}),
      confirmed: true,
    },
  })
  return {
    actionId: response?.actionId || actionId,
    finding: response?.finding || null,
    result: response?.result || null,
  }
}

/** Record that a supplier draft was copied / opened. Nothing is sent. */
export async function markOllieDraftUsed({
  venueId,
  checkId,
  actionId,
  via,
  supplierId,
} = {}) {
  requireValue(actionId, 'actionId')
  if (!OLLIE_DRAFT_USED_VIA.includes(via))
    throw new Error('via must be copy, email or whatsapp')
  await API.post('api2', `ollie/actions/${actionId}/used`, {
    body: {
      ...actionBody({ venueId, checkId }),
      via,
      ...(supplierId ? { supplierId } : {}),
    },
  })
  return { actionId, via }
}

/** "This isn't right" on a finding. */
export async function reportOllieFinding({
  venueId,
  checkId,
  reason,
  comment = '',
  surface,
} = {}) {
  requireValue(checkId, 'checkId')
  if (!OLLIE_REPORT_REASONS.some(entry => entry.id === reason)) {
    throw new Error('Pick a reason')
  }
  const response = await API.post('api2', `ollie/findings/${checkId}/report`, {
    body: {
      ...actionBody({ venueId, checkId }),
      reason,
      ...(comment ? { comment } : {}),
      ...(surface ? { surface } : {}),
    },
  })
  return {
    reported: Boolean(response?.reported),
    emailed: Boolean(response?.emailed),
    followUp: response?.followUp || null,
  }
}

/**
 * Shown / clicked / snoozed / dismissed / completed on a finding, through the
 * Growth Engine interaction route (`intentId: ollie:<checkId>`). Snooze,
 * dismiss and complete need the fingerprint the person saw.
 */
export async function postOllieFindingInteraction(
  venueId,
  { finding, interaction, surface } = {},
) {
  if (!finding?.checkId) throw new Error('finding is required')
  return postGrowthEngineRecommendationInteraction(venueId, {
    intentId: finding.intentId || ollieIntentIdForCheck(finding.checkId),
    interaction,
    featureId: finding.featureId || undefined,
    platform: OLLIE_PLATFORM,
    metadata: {
      ...(finding.fingerprint ? { fingerprint: finding.fingerprint } : {}),
      ...(surface ? { surface } : {}),
    },
  })
}
